import { ConnectedRouter } from 'connected-next-router';
import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { ActionBarProvider } from '@air/provider-action-bar';
import { ModalProvider } from '@air/provider-modal';
import { ShortIdProvider, type ShortIdProviderProps } from '@air/provider-short-id';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { PublicBoardReduxProvider } from '~/components/PublicBoardReduxProvider';
import { PublicBoardSocketContextProvider } from '~/components/PublicBoardSocketContextProvider';
import { PublicPermissionsProvider } from '~/components/PublicPermissionsProvider';
import { PublicRootBoardProvider } from '~/components/PublicRootBoardProvider';
import { PublicShareWorkspaceAccessProvider } from '~/components/PublicShareWorkspaceAccessProvider';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { PublicBoardPage } from '~/components/v2/components/board/PublicBoardPage';
import { KanbanOffsetContextProvider } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { TableHeaderPositionContextProvider } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import { useUI2026Styles } from '~/components/v2/hooks/useUI2026Styles';
import { ThumbnailPreferenceContextProvider } from '~/hooks/useThumbnailPreference';
import { PublicAccountProvider } from '~/providers/AccountProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { FiltersProvider, type GetImmutableFilters } from '~/providers/FiltersProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { PublicBoardCustomFieldsProvider } from '~/providers/PublicBoardCustomFieldsProvider';
import { SearchHeaderHeightProvider } from '~/providers/SearchHeaderHeightProvider';
import { type ClipSeoData, type PublicBoardShareData } from '~/swr-hooks/shortUrl/utils';
import { getBoardIdFromPath } from '~/utils/PathUtils';

export type PublicBoardLayoutProps = {
  board: PublicBoardShareData['board'];
  workspace: PublicBoardShareData['workspace'];
  permissions: PublicBoardShareData['permissions'];
  presentationSettings?: PublicBoardShareData['presentationSettings'];
  requireAccount: PublicBoardShareData['requireAccount'];
  initialClipData?: ClipSeoData | null;
  shortIdProps: Omit<ShortIdProviderProps, 'children'>;
};

export const PublicBoardLayout = memo(
  ({
    board,
    workspace,
    permissions,
    presentationSettings,
    requireAccount,
    initialClipData,
    shortIdProps,
  }: PublicBoardLayoutProps) => {
    useUI2026Styles();
    const { asPath } = useRouter();
    const { shortId } = shortIdProps;
    const boardId = getBoardIdFromPath(asPath);

    const getImmutableFilters = useCallback<GetImmutableFilters>(
      () => ({
        boardId,
      }),
      [boardId],
    );

    return (
      <PublicBoardReduxProvider initialBoard={board} presentationSettings={presentationSettings}>
        <ExternalLinksClickableProvider areExternalLinksClickable={shortIdProps.isOnPaidPlan === true}>
          <ShortIdProvider {...shortIdProps}>
            <AccountProvisionerProvider>
              <PublicAccountProvider workspace={workspace}>
                <PublicPermissionsProvider permissions={permissions}>
                  <PublicWorkspaceProvider
                    workspaceId={workspace.id}
                    workspaceImage={workspace.image}
                    workspaceName={workspace.name}
                  >
                    <PublicShareWorkspaceAccessProvider>
                      <ErrorBoundary>
                        <ConnectedRouter>
                          <SearchHeaderHeightProvider>
                            <FiltersProvider getImmutableFilters={getImmutableFilters}>
                              <ModifiersKeysPressedProvider>
                                <ModalProvider>
                                  <ThumbnailPreferenceContextProvider>
                                    <MaintenanceMode>
                                      <PublicBoardCustomFieldsProvider>
                                        <PublicBoardSocketContextProvider>
                                          <ActionBarProvider>
                                            <TableHeaderPositionContextProvider>
                                              <KanbanOffsetContextProvider>
                                                <PublicRootBoardProvider board={board}>
                                                  <PublicBoardPage
                                                    board={board}
                                                    presentationSettings={presentationSettings}
                                                    requireAccount={requireAccount}
                                                    initialClipData={initialClipData}
                                                    shortId={shortId}
                                                  />
                                                </PublicRootBoardProvider>
                                              </KanbanOffsetContextProvider>
                                            </TableHeaderPositionContextProvider>
                                          </ActionBarProvider>
                                        </PublicBoardSocketContextProvider>
                                      </PublicBoardCustomFieldsProvider>
                                    </MaintenanceMode>
                                  </ThumbnailPreferenceContextProvider>
                                </ModalProvider>
                              </ModifiersKeysPressedProvider>
                            </FiltersProvider>
                          </SearchHeaderHeightProvider>
                        </ConnectedRouter>
                      </ErrorBoundary>
                    </PublicShareWorkspaceAccessProvider>
                  </PublicWorkspaceProvider>
                </PublicPermissionsProvider>
              </PublicAccountProvider>
            </AccountProvisionerProvider>
          </ShortIdProvider>
        </ExternalLinksClickableProvider>
      </PublicBoardReduxProvider>
    );
  },
);

PublicBoardLayout.displayName = 'PublicBoardLayout';
